import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { fmt } from '../format';
import { useSession } from '../session';
import { ErrorBanner, Modal, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';
import { QrCode } from '../ui/QrCode';
import { ChangePassword } from './ChangePassword';

interface TwoFactorStatus {
  readonly enabled: boolean;
  readonly enabledAt: string | null;
  readonly recoveryCodesLeft: number;
  readonly required: boolean;
}

interface Setup {
  readonly secret: string;
  readonly otpauthUri: string;
  readonly setupToken: string;
}

/** Güvenlik: the user's own password and two-factor sign-in. */
export function Security() {
  return (
    <div className="stack">
      <TwoFactorPanel />
      <ChangePassword />
    </div>
  );
}

function TwoFactorPanel() {
  const status = useLoad(() => api<TwoFactorStatus>('GET', '/api/auth/2fa'), []);
  const session = useSession();
  const toast = useToast();
  const [setting, setSetting] = useState(false);
  const [confirming, setConfirming] = useState<'disable' | 'codes' | null>(null);
  const [codes, setCodes] = useState<readonly string[] | null>(null);

  if (status.error) return <ErrorBanner message={status.error} onRetry={() => void status.reload()} />;
  if (!status.data) return <Spinner />;
  const s = status.data;

  return (
    <section className="card narrow">
      <div className="card-h">
        <Icon name="shield" />
        <h2>İki adımlı doğrulama</h2>
        <span className={`chip ${s.enabled ? 'ok' : s.required ? 'bad' : ''}`}>{s.enabled ? 'Açık' : 'Kapalı'}</span>
      </div>
      <div className="card-b stack">
        {codes ? (
          <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
        ) : setting ? (
          <TwoFactorSetup
            onCancel={() => setSetting(false)}
            onEnabled={async (recovery) => {
              setSetting(false);
              setCodes(recovery);
              await status.reload();
              await session.reload();
            }}
          />
        ) : s.enabled ? (
          <>
            <p>
              Girişte şifrenizden sonra telefonunuzdaki doğrulama uygulamasının kodu da sorulur
              {s.enabledAt ? ` (${fmt.dateTime(s.enabledAt)} tarihinden beri açık)` : ''}. Kullanılmamış kurtarma kodu: <b>{s.recoveryCodesLeft}</b>
            </p>
            <div className="row">
              <button className="btn" onClick={() => setConfirming('codes')}>
                <Icon name="refresh" size={16} /> Yeni kurtarma kodları
              </button>
              {s.required ? (
                <span className="muted" style={{ fontSize: 13 }}>
                  Rolünüzde zorunlu olduğu için kapatılamaz.
                </span>
              ) : (
                <button className="btn ghost" onClick={() => setConfirming('disable')}>
                  Kapat
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p>
              Şifreniz başkasının eline geçse bile hesabınıza girilemesin: girişte telefonunuzdaki doğrulama uygulamasının (Google Authenticator, Microsoft
              Authenticator vb.) 30 saniyede bir değişen kodu da sorulur.
            </p>
            <div className="row">
              <button className="btn primary" onClick={() => setSetting(true)}>
                <Icon name="shield" size={16} /> Kurulumu başlat
              </button>
            </div>
          </>
        )}
      </div>
      {confirming && (
        <ConfirmIdentityDialog
          title={confirming === 'disable' ? 'İki adımlı doğrulamayı kapat' : 'Yeni kurtarma kodları'}
          action={confirming === 'disable' ? 'Kapat' : 'Yeni kodları oluştur'}
          onClose={() => setConfirming(null)}
          onConfirm={async (password, code) => {
            if (confirming === 'disable') {
              await api('POST', '/api/auth/2fa/disable', { password, code });
              toast('İki adımlı doğrulama kapatıldı', 'ok');
            } else {
              const result = await api<{ recoveryCodes: string[] }>('POST', '/api/auth/2fa/recovery-codes', { password, code });
              setCodes(result.recoveryCodes);
            }
            setConfirming(null);
            await status.reload();
            await session.reload();
          }}
        />
      )}
    </section>
  );
}

/** QR code → code from the app → recovery codes. Also the forced screen of roles that need it. */
export function TwoFactorSetup(props: { onEnabled: (recoveryCodes: readonly string[]) => Promise<void> | void; onCancel?: () => void }) {
  const setup = useLoad(() => api<Setup>('POST', '/api/auth/2fa/setup'), []);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!setup.data) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ recoveryCodes: string[] }>('POST', '/api/auth/2fa/enable', { setupToken: setup.data.setupToken, code: code.replace(/\s/g, '') });
      await props.onEnabled(result.recoveryCodes);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  if (setup.error) return <ErrorBanner message={setup.error} onRetry={() => void setup.reload()} />;
  if (!setup.data) return <Spinner />;
  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <ol className="steps">
        <li>Telefonunuza bir doğrulama uygulaması kurun: Google Authenticator, Microsoft Authenticator veya benzeri.</li>
        <li>Uygulamada "hesap ekle / QR kodu tara" deyip aşağıdaki kodu okutun.</li>
        <li>Uygulamanın gösterdiği 6 haneli kodu yazın.</li>
      </ol>
      <div className="qr-box">
        <QrCode text={setup.data.otpauthUri} label="Doğrulama uygulaması için QR kod" />
        <div className="stack" style={{ gap: 6 }}>
          <span className="muted" style={{ fontSize: 13 }}>
            QR okutamıyorsanız uygulamaya bu anahtarı elle girin:
          </span>
          <span className="mono secret-key">{setup.data.secret.match(/.{1,4}/g)?.join(' ')}</span>
        </div>
      </div>
      {error && <div className="banner bad">{error}</div>}
      <div className="field">
        <label htmlFor="totp-code">Uygulamadaki kod</label>
        <input
          id="totp-code"
          className="input mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ maxWidth: 180, fontSize: 22, letterSpacing: 4 }}
          required
        />
      </div>
      <div className="row">
        <button className="btn primary" disabled={busy || code.replace(/\s/g, '').length !== 6}>
          Doğrula ve aç
        </button>
        {props.onCancel && (
          <button type="button" className="btn ghost" onClick={props.onCancel}>
            Vazgeç
          </button>
        )}
      </div>
    </form>
  );
}

/** Shown once: the codes that open the account when the phone is lost. */
function RecoveryCodes({ codes, onDone }: { codes: readonly string[]; onDone: () => void }) {
  const toast = useToast();
  const [saved, setSaved] = useState(false);
  return (
    <div className="stack">
      <div className="banner ok">
        <Icon name="check" />
        <div>
          İki adımlı doğrulama açık. Telefonunuzu kaybederseniz aşağıdaki kodlardan biriyle girebilirsiniz; her kod bir kez çalışır.
          <div style={{ fontWeight: 500 }}>Kodlar bir daha gösterilmez: şifre yöneticinize kaydedin veya yazıp güvenli bir yerde saklayın.</div>
        </div>
      </div>
      <div className="recovery-codes mono">
        {codes.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      <div className="row">
        <button className="btn" onClick={() => void navigator.clipboard?.writeText(codes.join('\n')).then(() => toast('Kodlar kopyalandı', 'ok'))}>
          Kopyala
        </button>
        <label className="row" style={{ gap: 6 }}>
          <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> Kodları güvenli bir yere kaydettim
        </label>
        <button className="btn primary" disabled={!saved} onClick={onDone}>
          Tamam
        </button>
      </div>
    </div>
  );
}

function ConfirmIdentityDialog(props: { title: string; action: string; onClose: () => void; onConfirm: (password: string, code: string) => Promise<void> }) {
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await props.onConfirm(password, code.trim());
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || !password || code.trim().length < 6} onClick={() => void submit()}>
            {props.action}
          </button>
        </>
      }
    >
      <div className="stack">
        {error && <div className="banner bad">{error}</div>}
        <div className="field">
          <label htmlFor="confirm-password">Şifre</label>
          <input id="confirm-password" className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="confirm-code">Uygulamadaki kod veya kurtarma kodu</label>
          <input id="confirm-code" className="input mono" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/** Roles that need two-factor sign-in see this instead of the app until it is on. */
export function TwoFactorGate() {
  const session = useSession();
  const [codes, setCodes] = useState<readonly string[] | null>(null);
  return (
    <div className="login">
      <div className="card" style={{ maxWidth: 560 }}>
        <div className="card-h">
          <Icon name="shield" />
          <h2>İki adımlı doğrulamayı açın</h2>
          <span className="sub">{session.me.email}</span>
        </div>
        <div className="card-b stack">
          {codes ? (
            <RecoveryCodes codes={codes} onDone={() => void session.reload()} />
          ) : (
            <>
              <div className="banner warn">
                <Icon name="key" />
                <div>Depo yöneticisi hesapları tüm satıcıların verisine erişir; bu yüzden girişte şifreye ek olarak telefonunuzdaki kod da sorulacak.</div>
              </div>
              <TwoFactorSetup onEnabled={(recovery) => setCodes(recovery)} />
              <button className="btn ghost" onClick={session.logout}>
                Çıkış yap
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
